import React from "react";
import PropTypes from "prop-types";

/**
 * WebhookStatusDisplay - Shows real-time status of webhook operations
 */
const WebhookStatusDisplay = ({
  isRunning,
  status,
  result,
  error,
  operationName = "Operation",
  onCancel,
  onReset,
}) => {
  if (!isRunning && !result && !error) {
    return null;
  }

  return (
    <div className="mt-4 p-4 rounded-lg border">
      {/* Running Status */}
      {isRunning && (
        <div className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div>
                <h4 className="text-blue-800 font-medium">
                  {operationName} Running
                </h4>
                {status && (
                  <p className="text-blue-700 text-sm">Status: {status}</p>
                )}
              </div>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded border border-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                🛑 Cancel Job
              </button>
            )}
          </div>

          {/* Progress Information */}
          {status && (
            <div className="mt-2 p-2 bg-blue-100 rounded text-blue-800 text-sm">
              📋 {status}
            </div>
          )}
        </div>
      )}

      {/* Success Result */}
      {result && !isRunning && (
        <div className="bg-green-50 border-green-200">
          <div className="flex items-center space-x-2 mb-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h4 className="text-green-800 font-medium">
              {operationName} Completed
            </h4>
          </div>

          <div className="text-green-700 text-sm space-y-1">
            {/* Display results based on operation type */}
            {result.articles_processed !== undefined && (
              <p>📰 Articles processed: {result.articles_processed}</p>
            )}
            {result.stories_created !== undefined && (
              <p>📖 Stories created: {result.stories_created}</p>
            )}
            {result.storiesAfter !== undefined &&
              result.storiesBefore !== undefined && (
                <p>
                  📊 Stories: {result.storiesBefore} → {result.storiesAfter}{" "}
                  (removed {result.duplicatesRemoved} duplicates)
                </p>
              )}
            {result.cleaned !== undefined && (
              <p>
                🧹 Orphaned stories cleaned: {result.cleaned} of {result.total}
              </p>
            )}
            {result.timestamp && (
              <p>⏰ Completed: {new Date(result.timestamp).toLocaleString()}</p>
            )}
            {result.message && <p>�� {result.message}</p>}
          </div>

          {/* Detailed results if available */}
          {result.details && (
            <details className="mt-2">
              <summary className="text-green-700 cursor-pointer hover:text-green-900">
                View detailed results
              </summary>
              <pre className="mt-2 p-2 bg-green-100 rounded text-xs overflow-auto">
                {JSON.stringify(result.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Error State */}
      {error && !isRunning && (
        <div className="bg-red-50 border-red-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h4 className="text-red-800 font-medium">
                {operationName} Failed
              </h4>
            </div>
            {onReset && (
              <button
                onClick={onReset}
                className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                🔄 Reset
              </button>
            )}
          </div>
          <p className="text-red-700 text-sm">❌ {error}</p>
        </div>
      )}

      {/* Reset button for completed operations */}
      {(result || error) && !isRunning && onReset && (
        <div className="mt-2 text-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            🔄 Clear Status
          </button>
        </div>
      )}
    </div>
  );
};

WebhookStatusDisplay.propTypes = {
  isRunning: PropTypes.bool.isRequired,
  status: PropTypes.string,
  result: PropTypes.object,
  error: PropTypes.string,
  operationName: PropTypes.string,
  onCancel: PropTypes.func,
  onReset: PropTypes.func,
};

export default WebhookStatusDisplay;
